import { LitElement, html, css } from "lit";
import { customElement, property } from "lit/decorators.js";

/**
 * bn-empty-state is the centred placeholder for empty lists, no-results and
 * not-found. It shows an optional glyph (an `icon` string — an emoji or a
 * single character), a `heading`, a `message`, and an `actions` slot for the
 * call to action. It is also what bn-table projects into its `empty` slot.
 *
 * Properties:
 * - `icon` — decorative glyph, hidden from assistive technology.
 * - `heading` — the short statement of what is missing.
 * - `message` — one sentence explaining what to do about it.
 *
 * Slots:
 * - `actions` — the call to action, usually a single primary button.
 *
 *   <bn-empty-state icon="◲" heading="No instances yet"
 *                   message="Create one to start serving queries.">
 *     <bn-button slot="actions" variant="primary">New instance</bn-button>
 *   </bn-empty-state>
 */
@customElement("bn-empty-state")
export class BnEmptyState extends LitElement {
  /** Decorative glyph shown above the heading; never announced. */
  @property() icon = "";
  /** Short statement of what is missing. */
  @property() heading = "";
  /** One sentence explaining what to do about it. */
  @property() message = "";

  static override styles = css`
    :host {
      display: block;
    }
    .box {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      gap: var(--bino-space-sm);
      padding: var(--bino-space-xl) var(--bino-space-lg);
      color: var(--bino-text-secondary);
      font-family: var(--bino-font-sans);
    }
    .icon {
      font-size: var(--bino-font-size-xl);
      line-height: 1;
      color: var(--bino-text-secondary);
      opacity: 0.8;
    }
    .heading {
      font-size: var(--bino-font-size-md);
      font-weight: 600;
      color: var(--bino-text);
    }
    .message {
      font-size: var(--bino-font-size-base);
      /* A readable measure, expressed in characters rather than a length so it
         tracks the font size the theme sets. */
      max-width: 60ch;
    }
    .actions {
      margin-top: var(--bino-space-sm);
    }
  `;

  override render() {
    return html`
      <div class="box">
        ${this.icon ? html`<div class="icon" aria-hidden="true">${this.icon}</div>` : ""}
        ${this.heading ? html`<div class="heading">${this.heading}</div>` : ""}
        ${this.message ? html`<div class="message">${this.message}</div>` : ""}
        <div class="actions"><slot name="actions"></slot></div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bn-empty-state": BnEmptyState;
  }
}
